// src/shared/lib/react.tsx
import {
  Context,
  createContext,
  useContext,
  startTransition,
  useEffect,
  useState,
  ReactNode,
  Children,
  isValidElement,
  createElement,
  useMemo,
  useRef,
} from "react";

export function useStrictContext(context) {
  const value = useContext(context);
  if (value === null) throw new Error("Strict context not passed");
  return value;
}

export function createStrictContext() {
  return createContext(null);
}

export function useAppearanceDelay(show, options = {}) {
  const {
    minDisplay = 500,
    defaultValue = false,
    appearenceDelay = 500,
  } = options;

  const [delayedShow, setDelayedShow] = useState(defaultValue);

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        startTransition(() => setDelayedShow(true));
      }, appearenceDelay);
      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => {
        startTransition(() => setDelayedShow(false));
      }, minDisplay);
      return () => clearTimeout(timer);
    }
  }, [appearenceDelay, show, minDisplay]);

  return delayedShow;
}

export function ComposeChildren({ children }) {
  const array = Children.toArray(children);
  const last = array.pop();
  return (
    <>
      {array.reduceRight(
        (child, element) =>
          isValidElement(element)
            ? createElement(element.type, element.props, child)
            : child,
        last
      )}
    </>
  );
}

export function useEventCallback(fn) {
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  });
  return useMemo(
    () =>
      (...args) => {
        const { current } = ref;
        return current(...args);
      },
    []
  );
}

export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useDebouncedFilter(initialValue, setFilter, delay = 500) {
  const [localValue, setLocalValue] = useState(initialValue);
  const debouncedValue = useDebounce(localValue, delay);

  useEffect(() => {
    setFilter(debouncedValue);
  }, [debouncedValue, setFilter]);

  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  return [localValue, setLocalValue];
}

export function useScrollToError(errors) {
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const firstError = Object.keys(errors)[0];
      const errorElement =
        document.getElementsByName(firstError)[0] ||
        document.getElementById(firstError);

      errorElement?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [errors]);
}

export function useIntersectionObserver({ ref, rootMargin = "0px" }) {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );

    observer.observe(node);

    return () => {
      observer.unobserve(node);
    };
  }, [ref.current]); // важно! следить за ref.current

  return { isIntersecting };
}

const RegionCurrencySymbol = {
  GB: { symbol: "£", currencyCode: "GBP" },
  US: { symbol: "$", currencyCode: "USD" },
  FR: { symbol: "€", currencyCode: "EUR" },
  // Add more regions and their corresponding currency symbols and codes
};

export const useCurrencyFormat = (region) => {
  return useMemo(() => {
    const { symbol, currencyCode } = RegionCurrencySymbol[region];
    return (amount) => {
      const formattedAmount = amount.toLocaleString(`en-${region}`, {
        style: "currency",
        currency: currencyCode,
      });
      // return `${symbol}${formattedAmount}`;
      return `${formattedAmount.replace(/\.00$/, "")}`;
    };
  }, [region]);
};
